import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDStack,
  FDBodyText,
  FDBox,
} from '@facet-wealth/design';
import React, { useState } from 'react';
import { relationshipType } from 'facet-rest/src/consts/relationships';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { goToAccountOpeningFlow } from 'Helpers/Navigation';
import fr from 'facet-rest';
import { navigateToFunding } from 'Helpers/AccountFunding';
import OutcomeStat from 'Components/ComponentLibrary/Organisms/Outcome/OutcomeStat';
import PropTypes from 'prop-types';
import { navigateToManagedAccountPage } from 'Helpers/managedAccountPage';
import AdditionalInfoAccordion
  from 'Components/ComponentLibrary/Organisms/MemberDataCapture/Layouts/AdditionalInfoAccordion';
import ActionItemButton from 'Components/ComponentLibrary/Organisms/MemberDataCapture/Layouts/ActionItemButton';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';

export default function MemberDataCaptureCurrentActionItemLayout({ markCurrentAsComplete, collection, isReadOnly }) {
  const { resource } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();
  const [disableAllButtons, setDisableAllButtons] = useState(false);

  const additionalInformationIncludedResource = collection.included().list().find((el) => el.type() === 'memberDataCaptureAdditionalInformation');

  // link button relationships are supposed to navigate to another part of the application
  const linkButtonRelationships = resource.relationships().list().filter(links => links.meta('relationshipType') === relationshipType.linkButton);
  async function handleLinkButtonRelationshipClick(relationship) {
    setDisableAllButtons(true);
    try {
      switch (relationship.attr('name')) {
        case fr.relationships.accountOpeningWizard.name:
          await goToAccountOpeningFlow(resource, true);
          break;
        case fr.relationships.fundingInstructionsPage.name:
          await navigateToFunding(resource, true);
          break;
        case fr.relationships.managedAccountPage.name:
          await navigateToManagedAccountPage(resource, false, true);
          break;
        default:
      }
    } catch (e) {
      displayErrorMessage(e, 'Unable to proceed. Please try again later.');
    }
    setDisableAllButtons(false);
  }

  // response button relationships are supposed to be submitted
  const responseButtonRelationships = resource.relationships().list().filter(links => links.meta('relationshipType') === relationshipType.responseButton);
  async function handleResponseButtonRelationshipClick(relationship) {
    setDisableAllButtons(true);
    try {
      const [relationshipResource] = await relationship.link('related').get();
      await relationshipResource.submit();
      await markCurrentAsComplete();
    } catch (e) {
      displayErrorMessage(e, 'Unable to proceed. Please try again later.');
    }
    setDisableAllButtons(false);
  }

  async function handleAcknowledgeButtonRelationshipClick() {
    setDisableAllButtons(true);
    try {
      await markCurrentAsComplete();
    } catch (e) {
      displayErrorMessage(e, 'Unable to proceed. Please try again later.');
    }
    setDisableAllButtons(false);
  }

  const acknowledgeRelationship = resource.relationship(fr.relationships.acknowledge.name);

  const hasButtonRelationships = (linkButtonRelationships.length > 0) || (responseButtonRelationships.length > 0) || acknowledgeRelationship;
  const hasAssociatedOutcomes = resource.attr('associatedOutcomes')?.resources().length() > 0;

  return (
    <FDStack direction={'column'} alignItems={'stretch'} spacing={2}>
      <FDStack direction={'column'}>
        { additionalInformationIncludedResource
          ? (<AdditionalInfoAccordion additionalInformationIncludedResource={additionalInformationIncludedResource} currentSectionResource={resource} />)
          : (
            <FDBox>
              <MetaField attr={'title'} />
              <MetaField attr={'subTitle'} />
            </FDBox>
          )
        }
      </FDStack>

      <MetaField attr={'description'} />

      {hasButtonRelationships && (
        <FDStack direction={'row'} spacing={2} wrap>
          {linkButtonRelationships.map(relationship => (<ActionItemButton
            disabled={disableAllButtons || isReadOnly}
            dataTestValue={'mdc-link-button'}
            key={relationship.attr('name')}
            relationship={relationship}
            onClick={() => handleLinkButtonRelationshipClick(relationship)}
          />))}
          {responseButtonRelationships.map(relationship => (<ActionItemButton
            disabled={disableAllButtons || isReadOnly}
            dataTestValue={'mdc-response-button'}
            key={relationship.attr('name')}
            relationship={relationship}
            onClick={() => handleResponseButtonRelationshipClick(relationship)}
          />))}
          {
            acknowledgeRelationship
              ? (
                <ActionItemButton
                  disabled={disableAllButtons || isReadOnly}
                  dataTestValue={'mdc-acknowledgement-button'}
                  key={acknowledgeRelationship.attr('name')}
                  relationship={acknowledgeRelationship}
                  onClick={handleAcknowledgeButtonRelationshipClick}
                />
              ) : null
          }
        </FDStack>
      )}

      {hasAssociatedOutcomes && (
        <FDBox paddingY={1}>
          <FDStack direction={'column'} spacing={2}>
            <FDBodyText
              value={resource.attr('associatedOutcomesTitle')}
              size={'md'}
            />
            <FDStack spacing={2} wrap>
              {resource.attr('associatedOutcomes').resources().list().map(associatedOutcome => (
                <OutcomeStat
                  key={associatedOutcome.id()}
                  type={associatedOutcome.attr('type')}
                  title={associatedOutcome.attr('title')}
                  subtitle={associatedOutcome.attr('subTitle')}
                />
              ))}
            </FDStack>
          </FDStack>
        </FDBox>
      )}
    </FDStack>
  );
}
MemberDataCaptureCurrentActionItemLayout.propTypes = {
  markCurrentAsComplete: PropTypes.func.isRequired,
  collection: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
};
